import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';
import * as XLSX from 'xlsx';
import { ViewerSettings } from '@/types/fileViewer';

interface SpreadsheetViewerProps {
  fileUrl: string;
  fileName: string;
  settings: ViewerSettings;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  onError?: () => void;
}

type ColWidth = {
  wpx?: number;
  wch?: number;
};

type RowHeight = {
  hpx?: number;
  hpt?: number;
};

type CellStyle = {
  bgColor?: string;
  fgColor?: string;
  fontWeight?: number;
  fontStyle?: string;
};

type CellData = {
  value: string;
  style: CellStyle;
};

type SheetData = {
  name: string;
  data: CellData[][];
  colWidths: ColWidth[];
  rowHeights: RowHeight[];
  maxRow: number;
  maxCol: number;
};

// 파일 확장자 추출
const getFileExtension = (fileName: string): string => {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
};

// CSV 인코딩 감지 및 디코딩
const decodeCSV = async (arrayBuffer: ArrayBuffer): Promise<string> => {
  // 먼저 UTF-8로 시도
  const utf8Text = new TextDecoder('utf-8').decode(arrayBuffer);

  // BOM 체크 또는 한글 깨짐 체크
  const hasValidKorean = /[가-힣]/.test(utf8Text);
  const hasGarbage = /[�]/.test(utf8Text);

  if (hasValidKorean && !hasGarbage) {
    return utf8Text;
  }

  // EUC-KR (CP949) 시도
  try {
    const eucKrText = new TextDecoder('euc-kr').decode(arrayBuffer);
    if (/[가-힣]/.test(eucKrText)) {
      return eucKrText;
    }
  } catch {
    // EUC-KR 디코딩 실패시 UTF-8 반환
  }

  return utf8Text;
};

// ARGB를 RGB hex로 변환
const argbToHex = (argb: string): string => {
  if (!argb) return '';
  // ARGB 형식 (예: FF000000) -> RGB (000000)
  if (argb.length === 8) {
    return '#' + argb.slice(2);
  }
  return '#' + argb;
};

// 셀 스타일 추출
const getCellStyle = (cell: XLSX.CellObject | undefined): CellStyle => {
  const style: CellStyle = {};

  if (!cell?.s) return style;

  const cellStyle = cell.s as any;

  // 배경색
  if (cellStyle.fill?.fgColor?.rgb) {
    style.bgColor = argbToHex(cellStyle.fill.fgColor.rgb);
  } else if (cellStyle.fill?.bgColor?.rgb) {
    style.bgColor = argbToHex(cellStyle.fill.bgColor.rgb);
  }

  // 글자색
  if (cellStyle.font?.color?.rgb) {
    style.fgColor = argbToHex(cellStyle.font.color.rgb);
  }

  // 굵기
  if (cellStyle.font?.bold) {
    style.fontWeight = 700;
  }

  // 기울임
  if (cellStyle.font?.italic) {
    style.fontStyle = 'italic';
  }

  return style;
};

const SpreadsheetViewer: React.FC<SpreadsheetViewerProps> = ({
  fileUrl,
  fileName,
  settings,
  containerRef,
  onError,
}) => {
  const { zoom } = settings;
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width:768px)');

  useEffect(() => {
    const loadSpreadsheet = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(fileUrl);
        const arrayBuffer = await response.arrayBuffer();
        const ext = getFileExtension(fileName);

        let workbook: XLSX.WorkBook;

        // CSV는 인코딩 처리 필요
        if (ext === 'csv') {
          const text = await decodeCSV(arrayBuffer);
          workbook = XLSX.read(text, { type: 'string', cellStyles: true });
        } else {
          workbook = XLSX.read(arrayBuffer, {
            type: 'array',
            cellStyles: true,
          });
        }

        const parsedSheets: SheetData[] = workbook.SheetNames.map(
          (sheetName) => {
            const worksheet = workbook.Sheets[sheetName];

            // 범위 계산 (내용 있는 부분까지만)
            const range = XLSX.utils.decode_range(worksheet['!ref'] ?? 'A1');
            const maxRow = range.e.r + 1;
            const maxCol = range.e.c + 1;

            // 셀 데이터와 스타일 추출
            const data: CellData[][] = [];
            for (let r = 0; r < maxRow; r++) {
              const row: CellData[] = [];
              for (let c = 0; c < maxCol; c++) {
                const cellAddress = XLSX.utils.encode_cell({ r, c });
                const cell = worksheet[cellAddress];
                row.push({
                  value: cell?.v?.toString() ?? '',
                  style: getCellStyle(cell),
                });
              }
              data.push(row);
            }

            // 컬럼 너비 정보 추출
            const colWidths: ColWidth[] = worksheet['!cols'] ?? [];

            // 행 높이 정보 추출
            const rowHeights: RowHeight[] = worksheet['!rows'] ?? [];

            return {
              name: sheetName,
              data,
              colWidths,
              rowHeights,
              maxRow,
              maxCol,
            };
          },
        );

        setSheets(parsedSheets);
        setActiveSheet(0);
        setLoading(false);
      } catch (err) {
        setError('파일을 불러올 수 없습니다.');
        setLoading(false);
        onError?.();
        console.error(err);
      }
    };

    loadSpreadsheet();
  }, [fileUrl, fileName]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <CircularProgress sx={{ color: '#FFFFFF' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#FFFFFF',
          padding: '40px',
          textAlign: 'center',
        }}
      >
        {error}
      </Box>
    );
  }

  const currentSheet = sheets[activeSheet];
  const hasMultipleSheets = sheets.length > 1;

  // 컬럼 너비 계산 (기본값 80px)
  const getColWidth = (colIndex: number): number => {
    const col = currentSheet?.colWidths[colIndex];
    if (col?.wpx) return col.wpx;
    if (col?.wch) return col.wch * 8; // 문자 너비를 픽셀로 대략 변환
    return 80; // 기본값
  };

  // 행 높이 계산 (기본값 24px)
  const getRowHeight = (rowIndex: number): number => {
    const row = currentSheet?.rowHeights[rowIndex];
    if (row?.hpx) return row.hpx;
    if (row?.hpt) return row.hpt * 1.333; // pt to px
    return 24; // 기본값
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
      }}
    >
      {/* 패딩 영역 — 모바일은 여백 없이 꽉 차게 */}
      <Box
        sx={{
          padding: isMobile ? 0 : '32px 48px',
          boxSizing: 'border-box',
          minHeight: '100%',
        }}
      >
        {/* 컨텐츠 Wrapper - 둥근 모서리 + overflow hidden */}
        <Box
          sx={{
            borderRadius: isMobile ? 0 : '8px',
            border: isMobile
              ? 'none'
              : '1px solid var(--GrayScale-gray_3, #393D41)',
            boxShadow: isMobile
              ? 'none'
              : '0 0 16px 0 var(--effect-black-16, rgba(255, 255, 255, 0.16))',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            // 데스크톱: header(65) + 패딩(64) 만큼 빼서 카드를 뷰포트 안에 고정.
            // 모바일: 헤더가 2줄(~110px)로 가변적이고 주소창(dvh) 이슈도 있어 상수로
            // 빼지 않는다. 바깥 컨테이너(height:100% overflow:auto)가 스크롤을 담당한다.
            maxHeight: isMobile ? 'none' : 'calc(100vh - 65px - 64px)',
          }}
        >
          {/* 스크롤 가능한 테이블 영역 */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              background: '#FFFFFF',
            }}
          >
            <Box
              sx={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top left',
                transition: 'transform 0.2s ease-out',
                width: 'fit-content',
              }}
            >
              <TableContainer>
                <Table size="small" sx={{ width: 'auto' }}>
                  <TableHead>
                    <TableRow>
                      {currentSheet?.data[0]?.map((cell, index) => (
                        <TableCell
                          key={index}
                          sx={{
                            backgroundColor: cell.style.bgColor || '#FFFFFF',
                            color: cell.style.fgColor || '#000000',
                            fontWeight: cell.style.fontWeight || 400,
                            fontStyle: cell.style.fontStyle || 'normal',
                            borderBottom: '1px solid #E0E0E0',
                            borderRight: '1px solid #E0E0E0',
                            minWidth: getColWidth(index),
                            width: getColWidth(index),
                            height: getRowHeight(0),
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {cell.value}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentSheet?.data.slice(1).map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <TableCell
                            key={cellIndex}
                            sx={{
                              backgroundColor: cell.style.bgColor || '#FFFFFF',
                              color: cell.style.fgColor || '#000000',
                              fontWeight: cell.style.fontWeight || 400,
                              fontStyle: cell.style.fontStyle || 'normal',
                              borderBottom: '1px solid #E0E0E0',
                              borderRight: '1px solid #E0E0E0',
                              minWidth: getColWidth(cellIndex),
                              width: getColWidth(cellIndex),
                              height: getRowHeight(rowIndex + 1),
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {cell.value}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* 시트 탭 (하단 고정) */}
          {hasMultipleSheets && (
            <Box
              sx={{
                borderTop: '1px solid #E0E0E0',
                backgroundColor: '#F5F5F5',
                flexShrink: 0,
              }}
            >
              <Tabs
                value={activeSheet}
                onChange={(_, newValue) => setActiveSheet(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: '36px',
                  '& .MuiTab-root': {
                    minHeight: '36px',
                    minWidth: 'auto',
                    padding: '8px 16px',
                    color: '#666666',
                    fontSize: '13px',
                    textTransform: 'none',
                    borderRight: '1px solid #E0E0E0',
                    '&.Mui-selected': {
                      color: '#000000',
                      backgroundColor: '#FFFFFF',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none',
                  },
                }}
              >
                {sheets.map((sheet, index) => (
                  <Tab key={index} label={sheet.name} />
                ))}
              </Tabs>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SpreadsheetViewer;
